/**
 * PATH: packages/email-hub/src/feature/components/rich-text/EditorContextMenu.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-CTXMENU-001
 * PURPOSE: Lightweight right-click context menu for the email body editor —
 *          quick formatting plus the three "insert" affordances the plan calls
 *          for (table, image from device, image from QDrive, Kanban card).
 * WHY:     The browser's native menu can't trigger TipTap commands; this gives
 *          a discoverable, keyboard-dismissable surface without bloating
 *          RichTextEditor.tsx.
 * FLOW: contextmenu(x,y) → menu items → editor command / open-a-picker callback
 */

import { useLayoutEffect, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { createPortal } from 'react-dom'
import type { Editor } from '@tiptap/core'
import {
  Bold, ClipboardList, Copy, Image as ImageIcon, Italic, Link2, Sparkles,
  Table as TableIcon, Underline as UnderlineIcon, FolderOpen,
  Rows3, Columns3, Trash2,
} from 'lucide-react'
import { AI_SELECTION_ACTIONS, type AiSelectionAction } from '../../lib/aiCompose'

export interface CtxMenuPos { x: number; y: number; selectedText?: string }

interface Item {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  run: () => void
  active?: boolean
  divider?: boolean
}

export function EditorContextMenu({ editor, pos, onClose, onInsertImage, onInsertQDriveImage, onInsertCard, onInsertLink, onAiSelection, onAskAi }: {
  editor: Editor | null
  pos: CtxMenuPos | null
  onClose: () => void
  onInsertImage: () => void
  onInsertQDriveImage: () => void
  onInsertCard: () => void
  onInsertLink: () => void
  /** When provided AND there's a non-empty selection, the menu shows the AI
   *  polish actions (rewrite / shorten / tone / grammar) at the top. */
  onAiSelection?: (action: AiSelectionAction) => void
  /** Free-form "Ask AI…": always offered (with or without a selection) so the
   *  operator can ask AI to fill a table / draft a formatted email in place. */
  onAskAi?: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [adj, setAdj] = useState<CtxMenuPos | null>(null)

  // Clamp the menu inside the viewport once we know its size.
  useLayoutEffect(() => {
    if (!pos || !ref.current) { setAdj(pos); return }
    const r = ref.current.getBoundingClientRect()
    const x = Math.min(pos.x, window.innerWidth - r.width - 8)
    const y = Math.min(pos.y, window.innerHeight - r.height - 8)
    setAdj({ x: Math.max(8, x), y: Math.max(8, y), selectedText: pos.selectedText })
  }, [pos])

  useLayoutEffect(() => {
    if (!pos) return
    ref.current?.focus()
  }, [pos])

  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(!!pos, onClose, [ref])

  if (!pos || !editor) return null

  const run = (fn: () => void) => () => { fn(); onClose() }

  // AI polish actions only make sense over a highlighted range; hide them when
  // the caret is collapsed so the menu doesn't offer a no-op.
  const hasSelection = !editor.state.selection.empty
  // Free-form "Ask AI…" is offered regardless of selection; it can write fresh
  // content at the caret as well as transform a highlighted block.
  const askAiItems: Item[] = onAskAi
    ? [{ key: 'ask-ai', label: 'Ask AI…', icon: Sparkles, run: run(onAskAi) }]
    : []
  const aiItems: Item[] =
    onAiSelection && hasSelection
      ? AI_SELECTION_ACTIONS.map((action, i) => ({
          key: `ai-${action.id}`,
          label: action.label,
          icon: Sparkles,
          run: run(() => onAiSelection(action)),
          divider: i === 0 && askAiItems.length > 0,
        }))
      : []
  const topAi = [...askAiItems, ...aiItems]

  // Inside a table the operator needs row/column edits at their fingertips —
  // the toolbar's table row only appears when the caret is in a cell and is
  // easy to miss (operator 2026-06-29: "how do I add/delete columns/rows?").
  // So surface the same commands on right-click, and swap "Insert table" for
  // them so we never offer a nested table by accident.
  const inTable = editor.isActive('table')
  const tableItems: Item[] = inTable
    ? [
        { key: 'row-after', label: 'Add row below', icon: Rows3, divider: true, run: run(() => editor.chain().focus().addRowAfter().run()) },
        { key: 'row-before', label: 'Add row above', icon: Rows3, run: run(() => editor.chain().focus().addRowBefore().run()) },
        { key: 'col-after', label: 'Add column right', icon: Columns3, run: run(() => editor.chain().focus().addColumnAfter().run()) },
        { key: 'col-before', label: 'Add column left', icon: Columns3, run: run(() => editor.chain().focus().addColumnBefore().run()) },
        { key: 'row-del', label: 'Delete row', icon: Trash2, run: run(() => editor.chain().focus().deleteRow().run()) },
        { key: 'col-del', label: 'Delete column', icon: Trash2, run: run(() => editor.chain().focus().deleteColumn().run()) },
        { key: 'table-del', label: 'Delete table', icon: Trash2, run: run(() => editor.chain().focus().deleteTable().run()) },
      ]
    : [
        { key: 'table', label: 'Insert table', icon: TableIcon, run: run(() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()) },
      ]

  const selectedText = pos.selectedText?.trim() ?? ''
  const copySelection = () => {
    if (!selectedText) return
    void navigator.clipboard.writeText(selectedText).catch(() => undefined)
  }
  const copyItems: Item[] = selectedText
    ? [{ key: 'copy', label: 'Copy', icon: Copy, run: run(copySelection) }]
    : []

  const items: Item[] = [
    ...copyItems,
    ...topAi,
    { key: 'bold', label: 'Bold', icon: Bold, active: editor.isActive('bold'), run: run(() => editor.chain().focus().toggleBold().run()), divider: topAi.length > 0 || copyItems.length > 0 },
    { key: 'italic', label: 'Italic', icon: Italic, active: editor.isActive('italic'), run: run(() => editor.chain().focus().toggleItalic().run()) },
    { key: 'underline', label: 'Underline', icon: UnderlineIcon, active: editor.isActive('underline'), run: run(() => editor.chain().focus().toggleUnderline().run()) },
    { key: 'link', label: 'Insert link…', icon: Link2, divider: true, run: run(onInsertLink) },
    ...tableItems,
    { key: 'image', label: 'Insert image (upload)', icon: ImageIcon, run: run(onInsertImage) },
    { key: 'qdrive', label: 'Insert image from QDrive', icon: FolderOpen, run: run(onInsertQDriveImage) },
    { key: 'card', label: 'Insert Kanban card', icon: ClipboardList, run: run(onInsertCard) },
  ]

  const at = adj || pos

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label="Editor actions"
      className="fixed z-[95] min-w-52 rounded-lg border border-border bg-popover py-1 shadow-2xl"
      style={{ left: at.x, top: at.y }}
      tabIndex={-1}
      onKeyDown={(event) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'c') {
          if (!selectedText) return
          event.preventDefault()
          copySelection()
        }
      }}
      onCopy={(event) => {
        if (!selectedText) return
        event.preventDefault()
        event.clipboardData.setData('text/plain', selectedText)
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {items.map((it) => (
        <div key={it.key}>
          {it.divider && <div className="my-1 h-px bg-border" />}
          <button
            type="button"
            role="menuitem"
            onClick={it.run}
            className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm hover:bg-accent ${it.active ? 'text-primary' : 'text-foreground'}`}
          >
            <it.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{it.label}</span>
          </button>
        </div>
      ))}
    </div>,
    document.body,
  )
}
